import { Modal } from '@/components/admin/Modal';
import { ApiKeyFormModal } from '@/components/api-keys/ApiKeyFormModal';
import { RevealSecretDialog } from '@/components/api-keys/RevealSecretDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ApiKey, ApiKeyScope, ApiKeyWithSecret } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Activity, Ban, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function AdminApiKeys() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [scope, setScope] = useState<ApiKeyScope>('mine');
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState<ApiKeyWithSecret | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['admin', 'api-keys', 'settings'],
    queryFn: () => api.adminApiKeysSettings(),
  });

  const settingsMut = useMutation({
    mutationFn: (enabled: boolean) =>
      api.adminApiKeysSettingsUpdate({ llmApiRelayEnabled: enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'api-keys', 'settings'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '설정 변경에 실패했습니다.');
    },
  });

  const listQuery = useQuery({
    queryKey: ['admin', 'api-keys', 'list', scope],
    queryFn: () => api.adminApiKeysList(scope),
    refetchInterval: 30_000,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => api.adminApiKeyRevoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys', 'list'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '취소에 실패했습니다.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminApiKeyDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys', 'list'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const enabled = settingsQuery.data?.llmApiRelayEnabled ?? false;
  const keys = listQuery.data?.keys ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="API 인증 관리"
          count={keys.length}
          description="vLLM 등 백엔드 LLM 을 외부에서 호출할 수 있도록 OpenAI-compatible 프록시 키를 발급/관리합니다."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate({ to: '/admin/api-keys/usage' })}>
                <Activity className="size-4" />
                사용량
              </Button>
              <Button onClick={() => setCreateOpen(true)} disabled={!enabled}>
                <Plus className="size-4" />키 발급
              </Button>
            </div>
          }
        />
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
          <button
            type="button"
            className="ml-3 text-xs underline"
            onClick={() => setServerError(null)}
          >
            닫기
          </button>
        </div>
      ) : null}

      <section className="mb-6 rounded-2xl border border-border bg-surface p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">전역 활성화</h2>
            <p className="mt-1 text-sm text-foreground-muted">
              OFF 면 모든 키가 401/403 으로 거부됩니다. 사용자별 권한은{' '}
              <a className="underline" href="/admin/users">
                사용자 관리
              </a>{' '}
              에서 따로 설정합니다.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              disabled={settingsMut.isPending || settingsQuery.isLoading}
              onChange={(e) => settingsMut.mutate(e.target.checked)}
              className="size-4"
            />
            <span className="text-sm font-medium">{enabled ? 'ON' : 'OFF'}</span>
          </label>
        </div>
      </section>

      <div className="mb-3 flex items-center gap-2">
        <ScopeButton active={scope === 'mine'} onClick={() => setScope('mine')}>
          내 키
        </ScopeButton>
        <ScopeButton active={scope === 'all'} onClick={() => setScope('all')}>
          전체 사용자 키
        </ScopeButton>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : (
        <KeyTable
          keys={keys}
          showOwner={scope === 'all'}
          onRevoke={(id) => revokeMut.mutate(id)}
          onDelete={(id) => {
            if (window.confirm('정말 삭제할까요? 사용량 기록은 보존되지만 키 정보는 사라집니다.')) {
              deleteMut.mutate(id);
            }
          }}
        />
      )}

      <ApiKeyFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        variant="admin"
        onCreated={(k) => setReveal(k)}
      />
      <RevealSecretDialog open={!!reveal} onClose={() => setReveal(null)} secretKey={reveal} />

      {!enabled ? (
        <Modal
          open={!enabled && createOpen}
          onClose={() => setCreateOpen(false)}
          title="전역 활성화 필요"
        >
          <p className="text-sm">먼저 위의 '전역 활성화' 토글을 켜주세요.</p>
        </Modal>
      ) : null}
    </PageShell>
  );
}

function ScopeButton({
  active,
  onClick,
  children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-8 px-3 rounded-md text-sm border transition-colors',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-border bg-surface text-foreground-muted hover:bg-surface-muted',
      )}
    >
      {children}
    </button>
  );
}

function KeyTable({
  keys,
  showOwner,
  onRevoke,
  onDelete,
}: {
  keys: ApiKey[];
  showOwner: boolean;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th>이름</Th>
            <Th>접두어</Th>
            {showOwner ? <Th>소유자</Th> : null}
            <Th>만료</Th>
            <Th>마지막 사용</Th>
            <Th>상태</Th>
            <Th align="right">액션</Th>
          </tr>
        </thead>
        <tbody>
          {keys.length === 0 ? (
            <tr>
              <td colSpan={showOwner ? 7 : 6} className="text-center text-foreground-muted py-10">
                발급된 키가 없습니다.
              </td>
            </tr>
          ) : (
            keys.map((k) => (
              <tr key={k.id} className="border-t border-border hover:bg-surface-muted/30">
                <td className="px-4 py-3">
                  <div className="font-medium">{k.name}</div>
                  <div className="text-[11px] text-foreground-muted">
                    발급 {new Date(k.createdAt).toLocaleDateString('ko-KR')}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                {showOwner ? (
                  <td className="px-4 py-3 text-xs">
                    <div>{k.ownerDisplayName ?? '—'}</div>
                    <div className="text-foreground-muted">{k.ownerEmail ?? ''}</div>
                  </td>
                ) : null}
                <td className="px-4 py-3 text-xs">
                  {k.expiresAt ? new Date(k.expiresAt).toLocaleString('ko-KR') : '영구'}
                </td>
                <td className="px-4 py-3 text-xs">
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString('ko-KR') : '—'}
                </td>
                <td className="px-4 py-3">
                  <KeyStatusBadge k={k} />
                </td>
                <td className="px-4 py-3 text-right">
                  <KeyMenu
                    isRevoked={!!k.revokedAt}
                    onRevoke={() => onRevoke(k.id)}
                    onDelete={() => onDelete(k.id)}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function KeyStatusBadge({ k }: { k: ApiKey }) {
  if (k.revokedAt) {
    return (
      <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-destructive/15 text-destructive">
        취소됨
      </span>
    );
  }
  if (k.expiresAt && new Date(k.expiresAt).getTime() < Date.now()) {
    return (
      <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-600">
        만료됨
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider bg-success/15 text-success">
      활성
    </span>
  );
}

function KeyMenu({
  isRevoked,
  onRevoke,
  onDelete,
}: { isRevoked: boolean; onRevoke: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="w-48 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm text-left"
            >
              <button
                type="button"
                disabled={isRevoked}
                onClick={() => {
                  setOpen(false);
                  onRevoke();
                }}
                className={cn(
                  'flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
                  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
                )}
              >
                <Ban className="size-4" />
                {isRevoked ? '이미 취소됨' : '취소'}
              </button>
              <div className="my-1 border-t border-border" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted text-destructive"
              >
                <Trash2 className="size-4" />
                삭제
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
